import { Mail, MessageCircle, MapPin } from "lucide-react";

const TEAM = [
  {
    office: "Yiwu & Guangzhou, Chine",
    officeIcon: "🇨🇳",
    officeSubtitle: "Sourcing · Négociation · Préparation & expédition — équipe polyvalente selon le besoin client",
    members: [
      {
        name: "B. Kane",
        role: "PDG — Fondateur",
        email: "bkane@negosino.com",
        whatsapp: "8619878000805",
        bio: "Pilote la stratégie et les relations fournisseurs en Chine depuis 2012.",
        color: "from-[var(--accent)] to-[#D62828]",
      },
      {
        name: "A. Karim",
        role: "Partenaire — Sourcing & Business",
        email: "antakarim@negosino.com",
        whatsapp: "8618344323165",
        bio: "Déniche les produits rares et tendance, pilote la partie commerciale et apporte les idées business qui font la différence.",
        color: "from-[var(--secondary)] to-[#0a2559]",
      },
      {
        name: "Echo",
        role: "Responsable Colis & Expédition — Chine",
        email: "info@negosino.com",
        whatsapp: null,
        bio: "Réceptionne les colis chez les fournisseurs, contrôle, emballe et prépare chaque expédition avant le départ vers Dakar.",
        color: "from-[#DC143C] to-[#FFD700]",
      },
    ],
  },
  {
    office: "Dakar, Sénégal",
    officeIcon: "🇸🇳",
    officeSubtitle: "Réception colis · Contact clients · Livraison locale",
    members: [
      {
        name: "Ibrahima",
        role: "Responsable Réception Dakar",
        email: "info@negosino.com",
        whatsapp: "221774419060",
        bio: "Réceptionne les colis à l'aéroport et au port, contacte les clients à l'arrivée.",
        color: "from-[#FF6B00] to-[var(--accent)]",
      },
      {
        name: "Fatima",
        role: "Coordinatrice Livraison Dakar",
        email: "info@negosino.com",
        whatsapp: "221774419060",
        bio: "Organise les retraits, assiste les clients pour récupérer leur marchandise.",
        color: "from-[#00A86B] to-[var(--secondary)]",
      },
      {
        name: "Marème",
        role: "Responsable Ventes Boutique",
        email: "info@negosino.com",
        whatsapp: "221774419060",
        bio: "Accueille les clients à l'agence et gère les ventes en boutique à Dakar.",
        color: "from-[#8B5CF6] to-[var(--secondary)]",
      },
      {
        name: "Lala",
        role: "Finance & Transferts d'argent",
        email: "info@negosino.com",
        whatsapp: "221774419060",
        bio: "Gère la partie financière et les services de transfert d'argent avec la Chine.",
        color: "from-[#EC4899] to-[var(--accent)]",
      },
      {
        name: "Vieux",
        role: "Livreur Dakar & Régions",
        email: "info@negosino.com",
        whatsapp: "221774419060",
        bio: "Livre les colis dans Dakar et vers les régions du Sénégal, contact terrain avec les clients.",
        color: "from-[#0EA5E9] to-[var(--secondary)]",
      },
    ],
  },
];

function initialsFrom(name) {
  return String(name || "?")
    .trim()
    .split(/\s+/)
    .map((w) => w[0]?.toUpperCase())
    .join("")
    .slice(0, 2);
}

function formatWa(w) {
  if (!w) return "";
  // Format +86 198 7800 0805 style
  if (w.startsWith("86") && w.length === 13) {
    return `+86 ${w.slice(2,5)} ${w.slice(5,9)} ${w.slice(9)}`;
  }
  if (w.startsWith("221") && w.length === 12) {
    return `+221 ${w.slice(3,5)} ${w.slice(5,8)} ${w.slice(8,10)} ${w.slice(10)}`;
  }
  return `+${w}`;
}

export default function TeamSection() {
  return (
    <section className="mt-20 pt-16 border-t border-black/10" data-testid="team-section">
      <p className="kicker !text-[var(--secondary)]">Notre équipe</p>
      <h2 className="font-display text-4xl md:text-5xl font-black tracking-tighter mt-3">
        Des visages. <span className="text-[var(--accent)]">Deux continents.</span>
      </h2>
      <p className="text-[var(--ink-soft)] mt-4 max-w-2xl">
        Une équipe présente en Chine pour sourcer vos produits et négocier les meilleurs prix,
        et une équipe à Dakar pour vous livrer et vous accompagner en local.
      </p>

      <div className="mt-12 space-y-16">
        {TEAM.map((group) => (
          <div key={group.office} data-testid={`team-office-${group.office.split(",")[0].toLowerCase()}`}>
            <div className="flex items-center gap-3 mb-8">
              <span className="text-3xl">{group.officeIcon}</span>
              <div>
                <div className="flex items-center gap-2 text-[var(--accent)] font-bold text-sm uppercase tracking-widest">
                  <MapPin size={14} /> {group.office}
                </div>
                <p className="text-sm text-[var(--ink-soft)]">{group.officeSubtitle}</p>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {group.members.map((m) => (
                <div key={m.name} className="card-flat p-6 flex gap-5 items-start" data-testid={`team-member-${m.name.toLowerCase().replace(/\s+/g, "-").replace(/\./g, "")}`}>
                  <div className={`w-16 h-16 rounded-full bg-gradient-to-br ${m.color} text-white font-display font-black text-xl flex items-center justify-center flex-shrink-0 shadow-md`}>
                    {initialsFrom(m.name)}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-display text-xl font-bold">{m.name}</p>
                    <p className="text-xs uppercase tracking-widest text-[var(--accent)] font-bold mt-0.5">{m.role}</p>
                    <p className="text-sm text-[var(--ink-soft)] mt-3">{m.bio}</p>
                    <div className="flex flex-wrap gap-3 mt-4">
                      <a href={`mailto:${m.email}`} className="text-xs text-[var(--secondary)] hover:text-[var(--accent)] inline-flex items-center gap-1.5 font-semibold" data-testid={`team-email-${m.name.toLowerCase().replace(/\s+/g, "-").replace(/\./g, "")}`}>
                        <Mail size={13} /> {m.email}
                      </a>
                      {m.whatsapp && (
                        <a href={`https://wa.me/${m.whatsapp}`} target="_blank" rel="noopener noreferrer" className="text-xs text-[#25D366] hover:text-[#1ea855] inline-flex items-center gap-1.5 font-semibold" title={`WhatsApp ${formatWa(m.whatsapp)}`} data-testid={`team-whatsapp-${m.name.toLowerCase().replace(/\s+/g, "-").replace(/\./g, "")}`}>
                          <MessageCircle size={13} /> {formatWa(m.whatsapp)}
                        </a>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}
